import React, {
    Dispatch,
    ReactElement,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

import cogTokens from '@amzn/meridian-tokens/base/icon/cog';
import helpTokens from '@amzn/meridian-tokens/base/icon/help';
import Button from '@amzn/meridian/button';
import { ButtonProps, ButtonType } from '@amzn/meridian/button/button';
import Coachmark from '@amzn/meridian/coachmark';
import Icon from '@amzn/meridian/icon';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Tooltip from '@amzn/meridian/tooltip';

import { RateCardCoreRateGridColumnName } from 'src/constants';
import { CORE_PLAN_MAPPING } from 'src/constants/rateCards';
import { AppContext } from 'src/contexts/appContext';
import { CORE_RATE_GRID_PLAN_COLUMNS, ICoreRatesPlanConfiguration } from 'src/types/IRateCard';
import { IToast } from 'src/types/IToaster';

export interface IPlanConfigurator {
    readonly isEditMode: boolean;
    readonly planConfiguration: ICoreRatesPlanConfiguration;
    readonly setPlanConfiguration: Dispatch<ICoreRatesPlanConfiguration>;
    readonly buttonType?: ButtonProps['type'];
    readonly buttonSize?: ButtonProps['size'];
}

export function PlanConfigurator(props: IPlanConfigurator): ReactElement {
    const [modalOpen, setModalOpen] = useState(false);
    const { setToasts } = useContext(AppContext);

    useEffect(() => {
        const warnings = CORE_RATE_GRID_PLAN_COLUMNS.flatMap(
            (corePlan) => props.planConfiguration?.[corePlan]?.warnings ?? []
        );
        if (warnings.length > 0) {
            const messages = warnings.map((msg, idx) => `(${idx + 1}) ${msg}`).join('\n');
            const fullMessage = `Found the following issues with plan configuration: ${messages}`;
            setToasts((prev) => [
                ...prev,
                {
                    id: `PlanConfigurator`,
                    message: fullMessage,
                    type: 'error'
                }
            ]);
        }
    }, [props.planConfiguration]);

    const setCorePlanRoyaltyModelType = useCallback(
        (corePlan: RateCardCoreRateGridColumnName, newValue: string) => {
            props.setPlanConfiguration({
                ...props.planConfiguration,
                [corePlan]: {
                    ...props.planConfiguration[corePlan],
                    royaltyModelTypeCode: newValue
                }
            });
        },
        [props.planConfiguration]
    );

    const buttonText = props.isEditMode ? 'Configure Royalty Models' : 'View Royalty Models';

    return (
        <>
            <Button
                type={props.buttonType ?? 'primary'}
                size={props.buttonSize ?? 'small'}
                onClick={() => setModalOpen(true)}
            >
                <Icon tokens={cogTokens} />
                {buttonText}
            </Button>
            <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={buttonText} scrollContainer={'modal'}>
                <Table showStripes={true} headerRows={1} aria-role={'table'}>
                    <TableRow aria-role={'row'}>
                        <TableCell aria-role={'columnheader'}>Core Plan</TableCell>
                        <TableCell aria-role={'columnheader'}>Royalty Model Type</TableCell>
                    </TableRow>
                    {CORE_RATE_GRID_PLAN_COLUMNS.map((corePlan) => (
                        <TableRow key={corePlan}>
                            <TableCell aria-role={'cell'}>{corePlan}</TableCell>
                            <RoyaltyModelTypeCell
                                isEditMode={props.isEditMode}
                                value={props.planConfiguration?.[corePlan]?.royaltyModelTypeCode}
                                setValue={(value) => setCorePlanRoyaltyModelType(corePlan, value)}
                                options={CORE_PLAN_MAPPING[corePlan].royaltyModelTypeCodes}
                            />
                        </TableRow>
                    ))}
                </Table>
            </Modal>
        </>
    );
}

interface IRoyaltyModelTypeCell {
    readonly isEditMode: boolean;
    readonly value: string | undefined;
    readonly setValue: Dispatch<string>;
    readonly options: string[] | undefined;
}

function RoyaltyModelTypeCell(props: IRoyaltyModelTypeCell): ReactElement {
    const { isEditMode, value, setValue, options } = props;
    const canSelect = options && options.length > 1;
    const select = (
        <Select value={value} onChange={setValue} disabled={!canSelect}>
            {options && options.map((opt) => <SelectOption label={opt} value={opt} key={opt} />)}
        </Select>
    );

    const selectWithTooltip = (
        <Tooltip position={'end'} title={'Only 1 option'}>
            {select}
        </Tooltip>
    );

    return <TableCell aria-role={'cell'}>{isEditMode ? (canSelect ? select : selectWithTooltip) : value}</TableCell>;
}
